/**
 * dexRarity — what a caught entry is WORTH, and why.
 *
 * The Compare page can already say who has more. "More" is a poor contest: a
 * trainer three hundred behind on raw count may be sitting on the harder
 * collection, and telling them they are simply losing is how a comparison page
 * demotivates the person who most needs a reason to keep going. Dex Score is
 * the other question — not how MANY, but how HARD.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: quote odds. Niantic publishes none. Every
 * "1 in 512" in circulation is community datamining, and this app's whole claim
 * on the Compare page is that its numbers are true. So a score is built only
 * from things we know for certain — which dex an entry is in, and whether the
 * species is Legendary or Mythical — and the weights below are stated as
 * EFFORT, not probability. They are a judgement call, they are shown to the
 * trainer in the app, and they can be argued with; that is very different from
 * inventing a probability and presenting it as fact.
 *
 * Nor does it weight regional exclusives yet, though that is the first thing
 * anyone asks for. There is no clean region-locked roster in the codebase — the
 * nearest thing is a heuristic list inside the matchmaking scorer, weighted for
 * a different purpose entirely — and scoring a trainer's collection off a
 * heuristic would be exactly the kind of confident-but-unfounded number this
 * model exists to avoid. It goes in when the roster does.
 */

/** Every dex a catch can live in. Mirrors the server's HISTORY_VARIANTS. */
export const SCORED_VARIANTS = [
  "pokemon", "shiny", "lucky", "perfect", "xxl", "xxs",
  "shadow", "purified", "mega", "gmax", "dynamax", "shundo", "shinyLucky",
] as const;
export type ScoredVariant = (typeof SCORED_VARIANTS)[number];

export function isScoredVariant(v: string): v is ScoredVariant {
  return (SCORED_VARIANTS as readonly string[]).includes(v);
}

/**
 * Points for one entry in each dex, as a rough ranking of the WORK involved.
 *
 * The anchor is Main = 1: catching the thing at all. Everything else is read
 * against that.
 *
 * 100% OUTRANKS SHINY, AND BY A LOT. The instinct is the other way round —
 * shiny is the grind most trainers organise their playing around — but shiny
 * odds are ELASTIC. A Community Day, a raid day, an event: the rate moves by
 * more than an order of magnitude, and a patient trainer can bank shinies by
 * turning up on the right afternoon. A hundo cannot be farmed like that. Every
 * boost the game offers only raises the IV FLOOR — a raid or research catch
 * still has to roll the top of a narrowed range, and even a lucky trade, the
 * most generous floor in the game, only shortens the odds rather than removing
 * the roll. There is no afternoon you can turn up to that makes hundos common.
 * (Sam's call, 2026-08-13, and he is right.)
 *
 * Shundo — a shiny that is ALSO 100% — is not the sum of the two but far beyond
 * it, because both filters have to fall on the same catch.
 *
 * `complete` is absent on purpose: it is a DERIVED dex (a species you have
 * everywhere), not a thing you can go and catch, so scoring it would count the
 * same work twice.
 */
export const VARIANT_POINTS: Record<ScoredVariant, number> = {
  pokemon: 1,
  mega: 2,
  dynamax: 2,
  lucky: 3,
  shadow: 3,
  purified: 3,
  xxl: 4,
  xxs: 4,
  gmax: 5,
  shiny: 8,
  // A shiny you were also lucky with: the shiny grind, plus a trade.
  shinyLucky: 14,
  perfect: 18,
  shundo: 60,
};

/** How much harder the species itself is to come by, whatever the dex. */
export const MYTHICAL_MULTIPLIER = 3;
export const LEGENDARY_MULTIPLIER = 2;

export interface SpeciesRarityInput {
  /** Legendary — raid-gated, so every copy costs a raid. */
  legendary: boolean;
  /** Mythical — research or event gated, and often one per account ever. */
  mythical: boolean;
}

/**
 * The multiplier for a species. Mythical wins outright rather than stacking:
 * the two lists overlap in places, and a species cannot be gated twice over.
 */
export function speciesMultiplier(species: SpeciesRarityInput): number {
  if (species.mythical) return MYTHICAL_MULTIPLIER;
  if (species.legendary) return LEGENDARY_MULTIPLIER;
  return 1;
}

/** What one owned entry is worth. Zero for a dex that is not scored. */
export function entryPoints(variant: string, species: SpeciesRarityInput): number {
  if (!isScoredVariant(variant)) return 0;
  return VARIANT_POINTS[variant] * speciesMultiplier(species);
}

export interface ScoredEntry {
  variant: string;
  dexNumber: number;
}

export interface DexScore {
  /** The headline. */
  total: number;
  /** Entries that scored anything, so "points per catch" is honest. */
  scored: number;
  /** Points contributed by each dex, biggest first — the "why". */
  byVariant: Array<{ variant: ScoredVariant; points: number; entries: number }>;
}

/**
 * Score a whole collection.
 *
 * Deduplicated by (variant, species): a dex row is a statement that you own
 * that copy, and two rows for one copy — which forms can produce — is one
 * catch, not two.
 */
export function scoreDex(
  entries: readonly ScoredEntry[],
  rarityOf: (dexNumber: number) => SpeciesRarityInput,
): DexScore {
  const seen = new Set<string>();
  const buckets = new Map<ScoredVariant, { points: number; entries: number }>();
  let total = 0;
  let scored = 0;

  for (const e of entries) {
    if (!isScoredVariant(e.variant)) continue;
    const key = `${e.variant}:${e.dexNumber}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const points = entryPoints(e.variant, rarityOf(e.dexNumber));
    if (points <= 0) continue;
    total += points;
    scored += 1;
    const bucket = buckets.get(e.variant) ?? { points: 0, entries: 0 };
    bucket.points += points;
    bucket.entries += 1;
    buckets.set(e.variant, bucket);
  }

  return {
    total,
    scored,
    byVariant: [...buckets.entries()]
      .map(([variant, b]) => ({ variant, ...b }))
      .sort((a, b) => b.points - a.points || a.variant.localeCompare(b.variant)),
  };
}

/**
 * The hardest things in a collection, biggest first.
 *
 * Ties break towards the LOWER dex number, purely so the list is stable between
 * two renders of the same collection — with thirteen dexes and one multiplier,
 * ties are the common case, not the exception.
 */
export function rarestEntries(
  entries: readonly ScoredEntry[],
  rarityOf: (dexNumber: number) => SpeciesRarityInput,
  limit = 3,
): Array<ScoredEntry & { points: number }> {
  const seen = new Set<string>();
  const out: Array<ScoredEntry & { points: number }> = [];
  for (const e of entries) {
    if (!isScoredVariant(e.variant)) continue;
    const key = `${e.variant}:${e.dexNumber}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const points = entryPoints(e.variant, rarityOf(e.dexNumber));
    if (points > 0) out.push({ ...e, points });
  }
  return out
    .sort((a, b) => b.points - a.points || a.dexNumber - b.dexNumber)
    .slice(0, Math.max(0, limit));
}
